import { useState, useEffect } from "react";
import { MagnifyingGlass, Crosshair, Lightning, Target, SpinnerGap, Envelope, Megaphone, Plus, Pencil, Trash, X, FloppyDisk } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Sidebar({ filterZip, setFilterZip, onSearch, onDiscover, onAddNew, stats, discovering, activeCampaign, onSelectCampaign }) {
  const [discoverZip, setDiscoverZip] = useState("");
  const [discoverCity, setDiscoverCity] = useState("");
  const [discoverState, setDiscoverState] = useState("");
  const [businessType, setBusinessType] = useState("chiropractor");
  const [showCustom, setShowCustom] = useState(false);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState(emptyCampaign());
  const [saving, setSaving] = useState(false);

  const presets = [
    "chiropractor", "dentist", "plumber", "electrician",
    "personal injury lawyer", "real estate agent", "veterinarian",
    "accountant", "physical therapist", "auto mechanic",
    "roofing contractor", "HVAC technician", "family doctor", "optometrist",
  ];

  const handleDiscover = () => {
    if (!discoverZip.trim() && !discoverCity.trim()) return;
    onDiscover({
      zip_code: discoverZip.trim() || null,
      city: discoverCity.trim() || null,
      state: discoverState.trim() || null,
      business_type: businessType.trim() || "chiropractor",
    });
  };

  // Campaign CRUD
  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/campaigns`);
      setCampaigns(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) return;
    setSaving(true);
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign}`, campaignForm);
      } else {
        const res = await axios.post(`${API}/campaigns`, campaignForm);
        onSelectCampaign(res.data);
      }
      setShowCampaignForm(false);
      setEditingCampaign(null);
      setCampaignForm(emptyCampaign());
      fetchCampaigns();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      if (activeCampaign?.id === id) onSelectCampaign(null);
      fetchCampaigns();
    } catch (e) { console.error(e); }
  };

  const startEditing = (c) => {
    setEditingCampaign(c.id);
    setCampaignForm({ ...c });
    setShowCampaignForm(true);
  };

  return (
    <div className="hidden lg:flex w-[280px] flex-col border-r border-[--border] bg-white" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-[--border]">
        <h1 className="text-xl font-black tracking-tight text-[--text-primary]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          <span className="text-[--primary]">Pluck</span>m
        </h1>
        <p className="text-[10px] text-[--text-muted] mt-0.5 tracking-wide uppercase font-medium">SEO Outreach Engine</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Campaign Section */}
        <div className="p-4 border-b border-[--border]">
          <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[--primary] mb-2 flex items-center gap-1.5">
            <Megaphone size={11} weight="bold" />
            Campaign
          </label>

          {!showCampaignForm ? (
            <div className="space-y-1.5">
              {/* Default option */}
              <button
                onClick={() => onSelectCampaign(null)}
                data-testid="select-default-campaign"
                className={`w-full text-left px-3 py-2 text-xs border transition-colors ${
                  !activeCampaign ? "border-[--primary] bg-[--primary-light] font-medium text-[--primary]" : "border-[--border] hover:bg-[--bg-hover] text-[--text-secondary]"
                }`}
              >
                Default <span className="text-[--text-muted] font-normal">· SEO outreach</span>
              </button>

              {campaigns.map(c => (
                <div
                  key={c.id}
                  className={`flex items-center border transition-colors ${
                    activeCampaign?.id === c.id ? "border-[--primary] bg-[--primary-light]" : "border-[--border] hover:bg-[--bg-hover]"
                  }`}
                >
                  <button
                    onClick={() => onSelectCampaign(c)}
                    className="flex-1 text-left px-3 py-2 text-xs"
                    data-testid={`select-campaign-${c.id}`}
                  >
                    <span className={`font-medium ${activeCampaign?.id === c.id ? "text-[--primary]" : ""}`}>{c.name}</span>
                    {c.product_name && <span className="text-[--text-muted] ml-1">· {c.product_name}</span>}
                  </button>
                  <button onClick={() => startEditing(c)} className="p-1.5 text-[--text-muted] hover:text-[--primary]" data-testid={`edit-campaign-${c.id}`}>
                    <Pencil size={10} />
                  </button>
                  <button onClick={() => handleDeleteCampaign(c.id)} className="p-1.5 text-[--text-muted] hover:text-[--danger]" data-testid={`delete-campaign-${c.id}`}>
                    <Trash size={10} />
                  </button>
                </div>
              ))}

              <button
                data-testid="new-campaign-btn"
                onClick={() => { setCampaignForm(emptyCampaign()); setEditingCampaign(null); setShowCampaignForm(true); }}
                className="w-full flex items-center justify-center gap-1 py-2 border border-dashed border-[--border] text-[10px] text-[--text-muted] hover:text-[--primary] hover:border-[--primary] transition-colors"
              >
                <Plus size={10} /> New Campaign
              </button>
            </div>
          ) : (
            /* Inline campaign form */
            <div className="space-y-2 mt-1" data-testid="campaign-form">
              <CampaignInput label="Name *" value={campaignForm.name} onChange={v => setCampaignForm({...campaignForm, name: v})} placeholder="e.g. SlotSaver Launch" />
              <CampaignInput label="Product" value={campaignForm.product_name} onChange={v => setCampaignForm({...campaignForm, product_name: v})} placeholder="e.g. SlotSaver" />
              <CampaignArea label="What it does" value={campaignForm.product_description} onChange={v => setCampaignForm({...campaignForm, product_description: v})} placeholder="Online scheduling for local businesses..." />
              <CampaignArea label="Your pitch" value={campaignForm.pitch_angle} onChange={v => setCampaignForm({...campaignForm, pitch_angle: v})} placeholder="You're losing bookings because..." />
              <CampaignInput label="CTA" value={campaignForm.call_to_action} onChange={v => setCampaignForm({...campaignForm, call_to_action: v})} placeholder="Want me to run a free check?" />
              <div className="grid grid-cols-2 gap-2">
                <CampaignInput label="Sender" value={campaignForm.sender_name} onChange={v => setCampaignForm({...campaignForm, sender_name: v})} placeholder="Mike" />
                <CampaignInput label="Company" value={campaignForm.sender_company} onChange={v => setCampaignForm({...campaignForm, sender_company: v})} placeholder="SlotSaver" />
              </div>
              <CampaignArea label="Extra instructions" value={campaignForm.extra_instructions} onChange={v => setCampaignForm({...campaignForm, extra_instructions: v})} placeholder="Never mention competitors..." rows={2} />
              <div className="flex gap-1.5">
                <button
                  data-testid="save-campaign-btn"
                  onClick={handleSaveCampaign}
                  disabled={!campaignForm.name.trim() || saving}
                  className="flex-1 py-2 bg-[--primary] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[--primary-hover] disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingCampaign ? "Update" : "Save"}
                </button>
                <button
                  onClick={() => { setShowCampaignForm(false); setEditingCampaign(null); }}
                  className="px-3 py-2 text-[10px] text-[--text-muted] border border-[--border] hover:bg-[--bg-muted]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Discovery Section */}
        <div className="p-4 border-b border-[--border]">
          <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[--text-muted] mb-2 flex items-center gap-1.5">
            <Crosshair size={11} weight="bold" />
            Find Prospects
          </label>
          <div className="space-y-2">
            <div className="relative">
              <select
                data-testid="business-type-select"
                value={presets.includes(businessType) ? businessType : "__custom__"}
                onChange={(e) => {
                  if (e.target.value === "__custom__") { setBusinessType(""); setShowCustom(true); }
                  else { setBusinessType(e.target.value); setShowCustom(false); }
                }}
                className="w-full px-3 py-2 bg-[--bg-muted] border border-[--border] text-sm outline-none focus:border-[--primary] text-[--text-primary] capitalize cursor-pointer appearance-none"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%239CA3AF' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
              >
                {presets.map(p => (<option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>))}
                <option value="__custom__">Other (type your own)...</option>
              </select>
            </div>
            {showCustom && (
              <input data-testid="business-type-custom-input" type="text" autoFocus value={businessType} onChange={(e) => setBusinessType(e.target.value)}
                placeholder="Type business type..." className="w-full px-3 py-2 bg-[--bg-muted] border border-[--border] text-sm outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted]" />
            )}
            <div className="grid grid-cols-5 gap-2">
              <input data-testid="discover-city-input" type="text" value={discoverCity} onChange={(e) => setDiscoverCity(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleDiscover()} placeholder="City"
                className="col-span-2 px-3 py-2 bg-[--bg-muted] border border-[--border] text-sm outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted]" />
              <input data-testid="discover-state-input" type="text" value={discoverState} onChange={(e) => setDiscoverState(e.target.value)}
                placeholder="State" className="col-span-1 px-3 py-2 bg-[--bg-muted] border border-[--border] text-sm outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted]" />
              <input data-testid="discover-zip-input" type="text" value={discoverZip} onChange={(e) => setDiscoverZip(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleDiscover()} placeholder="ZIP"
                className="col-span-2 px-3 py-2 bg-[--bg-muted] border border-[--border] text-sm outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted]" />
            </div>
            <button data-testid="discover-btn" onClick={handleDiscover}
              disabled={(!discoverZip.trim() && !discoverCity.trim()) || discovering}
              className="w-full flex items-center justify-center gap-2 bg-[--primary] text-white px-4 py-2.5 text-sm font-medium hover:bg-[--primary-hover] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
              {discovering ? (<><SpinnerGap size={16} className="animate-spin" /> Searching...</>) : (<><Crosshair size={16} weight="bold" /> Discover Practices</>)}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="p-4">
          <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[--text-muted] mb-3 block">Overview</label>
          <div className="space-y-2">
            <StatItem icon={Target} label="Total Prospects" value={stats.total_prospects} />
            <StatItem icon={Lightning} label="Audited" value={stats.audited} color="var(--success)" />
            <StatItem icon={Envelope} label="Emails Ready" value={stats.emails_generated} color="var(--primary)" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-[--border]">
        <p className="text-[9px] text-[--text-muted] text-center uppercase tracking-widest">Any niche. Any ZIP. Any business.</p>
      </div>
    </div>
  );
}

function StatItem({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-3 p-2.5 border border-[--border] bg-[--bg-hover] hover:bg-[--bg-muted] transition-colors">
      <Icon size={16} weight="duotone" style={{ color: color || "var(--text-secondary)" }} />
      <div className="flex-1">
        <p className="text-[10px] text-[--text-muted] uppercase tracking-wider">{label}</p>
        <p className="text-base font-bold text-[--text-primary] leading-none mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function CampaignInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-[0.1em] text-[--text-muted] mb-0.5 block">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-2.5 py-1.5 bg-[--bg-muted] border border-[--border] text-xs outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted]" />
    </div>
  );
}

function CampaignArea({ label, value, onChange, placeholder, rows = 2 }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-[0.1em] text-[--text-muted] mb-0.5 block">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className="w-full px-2.5 py-1.5 bg-[--bg-muted] border border-[--border] text-xs outline-none focus:border-[--primary] text-[--text-primary] placeholder:text-[--text-muted] resize-none" />
    </div>
  );
}

function emptyCampaign() {
  return { name: "", product_name: "", product_description: "", pitch_angle: "", call_to_action: "", sender_name: "", sender_company: "", tone: "professional", extra_instructions: "" };
}
